import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { Check, Copy, Eye, EyeOff, TriangleAlert } from '@/lib/icons'
import { cn } from '@/lib/utils'
import type { PayoutCardResponse } from './api'

export interface PayoutCardCellProps {
  /** `null` — bloger/to'lovda karta yo'q (kartasiz holat). */
  maskedCard: string | null
  /** Auditlanadigan so'rov — faqat birinchi marta 👁 bosilganda chaqiriladi. */
  onReveal: () => Promise<PayoutCardResponse>
  className?: string
}

/**
 * Maskalangan karta raqami + 👁 ochish + nusxalash — `docs/03-app-flow.md` Sahifa 4:
 * "8600...1234 [👁]". Uchta joyda ishlatiladi (So'rovlar, To'lovga tayyor, Tarix), shuning
 * uchun bitta umumiy komponent — faqat `onReveal` chaqiruvi farq qiladi (to'lov nusxasimi,
 * blogerning joriy kartasimi, `api.ts`dagi izohga qarang).
 *
 * Vazifa talabi: "Never prefetch the full number; fetch only when the admin taps 👁" — shu
 * uchun mutatsiya FAQAT `handleToggle()` ichida, birinchi ochilishda chaqiriladi (keyingi
 * yashirish/ko'rsatishda takrorlanmaydi, xuddi `BloggerDetail.tsx`dagi naqsh kabi).
 *
 * "Copy-to-clipboard... eng yuqori qiymatli kichik funksiya" — admin bu raqamni bank
 * ilovasiga o'nlab marta qayta kiritadi, shuning uchun nusxalash tugmasi ochilgandan keyin
 * DARHOL ko'rinadi, alohida bosishni talab qilmaydi.
 */
export function PayoutCardCell({ maskedCard, onReveal, className }: PayoutCardCellProps) {
  const [revealed, setRevealed] = useState(false)
  const [copied, setCopied] = useState(false)

  const revealMutation = useMutation<PayoutCardResponse, Error, void>({
    mutationFn: onReveal,
  })

  if (!maskedCard) {
    return (
      <span
        className={cn(
          'inline-flex items-center gap-1 text-xs font-medium text-urgency-overdue-text',
          className,
        )}
      >
        <TriangleAlert size={13} aria-hidden="true" />
        Karta yo&apos;q
      </span>
    )
  }

  const displayed = revealed ? (revealMutation.data?.cardNumber ?? maskedCard) : maskedCard

  function handleToggle() {
    if (revealed) {
      setRevealed(false)
      setCopied(false)
      return
    }
    setRevealed(true)
    if (!revealMutation.data) revealMutation.mutate()
  }

  function handleCopy() {
    const value = revealMutation.data?.cardNumber
    if (!value) return
    void navigator.clipboard.writeText(value).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    })
  }

  return (
    <div className={cn('flex flex-col gap-0.5', className)}>
      <div className="flex items-center gap-1">
        <span className="tabular text-sm text-app-text">
          {revealed && revealMutation.isPending ? 'Yuklanmoqda...' : displayed}
        </span>
        <button
          type="button"
          onClick={handleToggle}
          aria-label={revealed ? 'Kartani yashirish' : "Kartani ko'rish"}
          title={revealed ? 'Yashirish' : "Ko'rish"}
          className="inline-flex size-11 shrink-0 items-center justify-center rounded-button text-app-hint transition-colors duration-150 ease-out hover:bg-app-card hover:text-brand"
        >
          {revealed ? (
            <EyeOff size={14} aria-hidden="true" />
          ) : (
            <Eye size={14} aria-hidden="true" />
          )}
        </button>
        {revealed && revealMutation.data && (
          <button
            type="button"
            onClick={handleCopy}
            aria-label="Karta raqamini nusxalash"
            title="Nusxalash"
            className={cn(
              'inline-flex size-11 shrink-0 items-center justify-center rounded-button transition-colors duration-150 ease-out hover:bg-app-card',
              copied ? 'text-urgency-done-text' : 'text-app-hint hover:text-brand',
            )}
          >
            {copied ? (
              <Check size={14} aria-hidden="true" />
            ) : (
              <Copy size={14} aria-hidden="true" />
            )}
          </button>
        )}
      </div>
      {revealed && revealMutation.error && (
        <span className="text-xs text-stage-rejected-text">{revealMutation.error.message}</span>
      )}
    </div>
  )
}
